/**
 * Helpers for interpreting Supabase Edge Function errors.
 *
 * When an Edge Function returns a non-2xx response, `supabase.functions.invoke`
 * resolves with `{ data: null, error }` where `error` is a `FunctionsHttpError`
 * whose `context` is the original `Response`. The structured error body
 * (`{ error, code }`) is therefore not on `data` - it must be read from the
 * response body. These helpers extract it safely.
 */

/** Structured error body returned by the Spotify edge functions. */
export interface EdgeFunctionErrorBody {
  error?: string;
  code?: string;
}

/** User-facing message shown when Spotify must be re-authorized. */
export const SPOTIFY_REAUTH_MESSAGE =
  'Spotify authorization expired. Please reconnect Spotify in Settings.';

/** Error code returned by edge functions when re-authorization is required. */
export const REAUTH_REQUIRED_CODE = 'REAUTH_REQUIRED';

/**
 * Read the structured error body from a failed `functions.invoke` error.
 * Returns null if the body is unavailable or not JSON.
 */
export async function readEdgeFunctionError(
  error: unknown
): Promise<EdgeFunctionErrorBody | null> {
  const context = (error as { context?: unknown } | null)?.context;

  if (context && typeof (context as Response).json === 'function') {
    try {
      // Clone so the body can still be consumed elsewhere if needed.
      const response = context as Response;
      const body = await (typeof response.clone === 'function'
        ? response.clone()
        : response
      ).json();
      return body as EdgeFunctionErrorBody;
    } catch {
      return null;
    }
  }

  return null;
}

/**
 * Error thrown when the user's Spotify authorization has expired and they must
 * sign in to Spotify again.
 */
export class SpotifyReauthRequiredError extends Error {
  readonly code = REAUTH_REQUIRED_CODE;

  constructor(message: string = SPOTIFY_REAUTH_MESSAGE) {
    super(message);
    this.name = 'SpotifyReauthRequiredError';
  }
}
